import React, { FC, ReactNode, useState } from 'react'
import { Column } from 'react-table'
import papaparse from 'papaparse'
import { t, Trans } from '@lingui/macro'

import {
  CertificationItemTypes,
  PathwayEnrolmentStatusEnum,
  WorkbookStatusEnum
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import IconDoc from '../../../components/common/icon/IconDoc'

import TableExtendedOld, {
  DEFAULT_TR_PROPS
} from '../../../components/common/table-extended-old/TableExtendedOld'
import { linkBuilder } from '../../../../../shared/linkBuilder'
import { navigateTo } from '../../../app/util/appHistory'
import IconMap from '../../../components/common/icon/IconMap'
import IconCalendar from '../../../components/common/icon/IconCalendar'
import Loader from '../../../components/common/loader/Loader'
import { downloadAsFile } from '../../../utils/downloadAsFile'
import { notification } from '../../../components/common/notifications/Notifications'
import { useCertificationEnrolmentItemsQuery } from './CertificationEnrolmentOverviewTable.codegen'
import {
  StatusProgressCompleted,
  StatusProgressDue,
  StatusProgressFailed,
  StatusProgressInProgress,
  StatusProgressNotStarted
} from 'front-end/src/components/common/status/Status'
import {
  StatusUserAttended,
  StatusUserDidNotAttend
} from '../../manage-events/components/event-status/EventStatus'
import { ButtonLight } from 'front-end/src/components'

interface IProps {
  certificationId: number
  certEnrolmentId: number
  userId: number
}

const CertificationEnrolmentOverviewTable: FC<IProps> = ({
  certificationId,
  certEnrolmentId,
  userId
}) => {
  const [downloadingCSV, setDownloadingCSV] = useState<boolean>(false)
  const { data, loading } = useCertificationEnrolmentItemsQuery({
    variables: {
      certificationId,
      certEnrolmentId,
      userId
    }
  })

  if (loading) {
    return <Loader />
  }

  const items = data?.certification?.items
  if (!items) {
    return null
  }

  const tableColumns: Array<Column<any>> = [
    {
      Header: t`Source`,
      Cell: (row) => {
        const title = getItemTitle(row.original)
        return <strong className="dark overflowEllipsis">{title}</strong>
      }
    },
    {
      Header: t`Type`,
      maxWidth: 200,
      Cell: (row) => {
        let itemType: ReactNode = null
        if (row.original.item_type === CertificationItemTypes.topic) {
          itemType = (
            <>
              <IconDoc /> <Trans>Resource</Trans>
            </>
          )
        } else if (row.original.item_type === CertificationItemTypes.pathway) {
          itemType = (
            <>
              <IconMap /> <Trans>Pathway</Trans>
            </>
          )
        } else if (row.original.item_type === CertificationItemTypes.event) {
          itemType = (
            <>
              <IconCalendar /> <Trans>Event</Trans>
            </>
          )
        }

        return <span className="grey">{itemType}</span>
      }
    },
    {
      Header: t`Status`,
      maxWidth: 160,
      accessor: 'topic',
      Cell: (row) => {
        if (row.original.item_type === CertificationItemTypes.topic) {
          if (!row.original.topic.certificationRelatedEnrolment) {
            return <StatusProgressNotStarted />
          }
          return getEnrolmentStatus(
            row.original.topic.certificationRelatedEnrolment.status
          )
        } else if (row.original.item_type === CertificationItemTypes.pathway) {
          if (!row.original.pathway.certificationRelatedEnrolment) {
            return <StatusProgressNotStarted />
          }
          return getEnrolmentStatus(
            row.original.pathway.certificationRelatedEnrolment.status
          )
        } else if (row.original.item_type === CertificationItemTypes.event) {
          if (!row.original.event.invitee) {
            return <StatusUserDidNotAttend />
          }
          return getEventAttendanceStatus(row.original.event.invitee.present)
        }
      }
    }
  ]

  const getItemTitle = (item) => {
    if (item.item_type === CertificationItemTypes.topic) {
      return item.topic.currentTitle
    } else if (item.item_type === CertificationItemTypes.pathway) {
      return item.pathway.title
    } else if (item.item_type === CertificationItemTypes.event) {
      return item.event.title
    }
  }

  const getCsvItemStatus = (item) => {
    if (item.item_type === CertificationItemTypes.topic) {
      return (
        item.topic.certificationRelatedEnrolment?.status ??
        WorkbookStatusEnum.NOT_STARTED
      )
    } else if (item.item_type === CertificationItemTypes.pathway) {
      return (
        item.pathway.certificationRelatedEnrolment?.status ??
        PathwayEnrolmentStatusEnum.NOT_STARTED
      )
    } else if (item.item_type === CertificationItemTypes.event) {
      if (item.event.invitee?.present) {
        return 'ATTENDED'
      }
      return 'NOT_ATTENDED'
    }
  }

  const downloadCsv = async () => {
    try {
      const csv = papaparse.unparse(
        items.map((row) => {
          return {
            Source: getItemTitle(row),
            Type:
              row.item_type === CertificationItemTypes.topic
                ? t`resource`
                : row.item_type,
            Status: getCsvItemStatus(row)
          }
        })
      )
      downloadAsFile(csv, 'looop-certification-enrolment-detail')
    } catch (err) {
      notification.error(t`CSV download failed`)
      throw err
    } finally {
      setDownloadingCSV(false)
    }
  }

  return (
    <TableExtendedOld
      data={items}
      columns={tableColumns}
      className="-highlight AnalyticsTable AnalyticsTable__certification-enrolments"
      defaultPageSize={50}
      showPageSizeOptions={false}
      getTrProps={(_state, row) => ({
        onClick: () => {
          if (row.original.item_type === CertificationItemTypes.topic) {
            if (!row.original.topic.certificationRelatedEnrolment) {
              notification.warning(t`No resource enrolment found`)
              return
            }
            navigateTo(
              linkBuilder.analyticsWorkbooksDetail(
                row.original.topic.certificationRelatedEnrolment.id
              ),
              true
            )
          } else if (
            row.original.item_type === CertificationItemTypes.pathway
          ) {
            navigateTo(
              linkBuilder.analyticsPathwayDetail(
                row.original.pathway.certificationRelatedEnrolment.id
              ),
              true
            )
          } else if (row.original.item_type === CertificationItemTypes.event) {
            navigateTo(
              linkBuilder.manageEvent(
                row.original.event.id,
                false,
                'attendance'
              ),
              true
            )
          }
        },
        className: 'clickable',
        ...DEFAULT_TR_PROPS
      })}
      resizable={false}
      sortable={false}
      minRows={1}
      paginationContentAfter={
        <ButtonLight
          isLoading={downloadingCSV}
          disabled={downloadingCSV}
          onClick={async () => {
            setDownloadingCSV(true)
            await downloadCsv()
          }}
        >
          <Trans>Export data</Trans> (.csv)
        </ButtonLight>
      }
    />
  )
}

const getEventAttendanceStatus = (status: boolean): ReactNode => {
  if (status) {
    return <StatusUserAttended />
  }
  return <StatusUserDidNotAttend />
}

const getEnrolmentStatus = (
  status: PathwayEnrolmentStatusEnum | WorkbookStatusEnum
): ReactNode => {
  switch (status) {
    case PathwayEnrolmentStatusEnum.COMPLETED || WorkbookStatusEnum.COMPLETED:
      return <StatusProgressCompleted />
    case PathwayEnrolmentStatusEnum.FAILED || WorkbookStatusEnum.FAILED:
      return <StatusProgressFailed />
    case PathwayEnrolmentStatusEnum.IN_PROGRESS ||
      WorkbookStatusEnum.IN_PROGRESS:
      return <StatusProgressInProgress />
    case PathwayEnrolmentStatusEnum.NOT_STARTED ||
      WorkbookStatusEnum.NOT_STARTED:
      return <StatusProgressNotStarted />
    case PathwayEnrolmentStatusEnum.OVERDUE || WorkbookStatusEnum.OVERDUE:
      return <StatusProgressDue status={WorkbookStatusEnum.OVERDUE} />
  }
}

export default CertificationEnrolmentOverviewTable
